import { flexRender, type Table as TanstackTable } from "@tanstack/react-table";
import type * as React from "react";

import { DataTablePagination } from "@/components/data-table/data-table-pagination";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getCommonPinningStyles } from "@/lib/data-table";
import { cn } from "@/lib/utils";

interface DataTableProps<TData> extends React.ComponentProps<"div"> {
  table: TanstackTable<TData>;
  actionBar?: React.ReactNode;
  isLoading?: boolean;
  loadingMessage?: React.ReactNode;
  emptyMessage?: React.ReactNode;
}

export function DataTable<TData>({
  table,
  actionBar,
  isLoading,
  loadingMessage,
  emptyMessage,
  children,
  className,
  ...props
}: DataTableProps<TData>) {
  return (
    <div
      className={cn("flex w-full flex-col gap-2.5 overflow-auto", className)}
      {...props}
    >
      {children}
      <div className="overflow-hidden rounded-md border">
        <div className="max-h-[calc(100vh-300px)] overflow-y-auto overflow-x-auto">
          <table className="w-full caption-bottom text-sm">
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const pinningStyles = getCommonPinningStyles({
                      column: header.column,
                      withBorder: true,
                    });
                    const isPinned = header.column.getIsPinned();
                    const zIndex = isPinned ? 20 : 10;
                    
                    return (
                      <TableHead
                        key={header.id}
                        colSpan={header.colSpan}
                        className={cn(
                          "sticky top-0 group",
                          isPinned ? "bg-background" : "bg-background",
                        )}
                        style={{
                          ...pinningStyles,
                          zIndex,
                          position: "sticky",
                          top: 0,
                          ...(isPinned
                            ? { backgroundColor: "var(--background)", opacity: 1 }
                            : {}),
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    className="group"
                  >
                    {row.getVisibleCells().map((cell) => {
                      const pinningStyles = getCommonPinningStyles({
                        column: cell.column,
                        withBorder: true,
                      });
                      const isPinned = cell.column.getIsPinned();
                      const isSelected = row.getIsSelected();

                      return (
                        <TableCell
                          key={cell.id}
                          className={cn(
                            isPinned && "bg-background group-hover:bg-muted",
                            isPinned && isSelected && "bg-muted",
                          )}
                          style={{
                            ...pinningStyles,
                            ...(isPinned && isSelected
                              ? { backgroundColor: "var(--muted)" }
                              : {}),
                            zIndex: isPinned ? 2 : undefined,
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              ) : isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={table.getAllColumns().length}
                    className="h-24 text-center"
                  >
                    {loadingMessage ?? "Loading..."}
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={table.getAllColumns().length}
                    className="h-24 text-center"
                  >
                    {emptyMessage ?? "No results."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </table>
        </div>
      </div>
      <div className="flex flex-col gap-2.5">
        <DataTablePagination table={table} />
        {actionBar &&
          table.getFilteredSelectedRowModel().rows.length > 0 &&
          actionBar}
      </div>
    </div>
  );
}
